import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSubscriptions, getSubscription, getAllSubscriptions, createSubscription, updateSubscription, deleteSubscription, updateSubscriptionStatus } from '../../api/subscriptions';

export const subscriptionKeys = {
    all: ['subscriptions'] as const,
    userList: (userId: string) => [...subscriptionKeys.all, 'list', userId] as const,
    details: (id: string) => [...subscriptionKeys.all, 'detail', id] as const,
    globalList: () => [...subscriptionKeys.all, 'global-list'] as const, // For admin views
};

export const useSubscriptions = (userId?: string, options?: { refetchInterval?: number | ((data: any) => number | false) }) => {
    return useQuery({
        queryKey: subscriptionKeys.userList(userId || ''),
        queryFn: () => getSubscriptions(userId!),
        enabled: !!userId,
        refetchInterval: options?.refetchInterval,
        refetchOnWindowFocus: 'always',
        refetchOnMount: 'always',
    });
};

export const useAllSubscriptions = () => {
    return useQuery({
        queryKey: subscriptionKeys.globalList(),
        queryFn: () => getAllSubscriptions(),
    });
};

export const useSubscription = (id?: string, options?: { refetchInterval?: number | ((data: any) => number | false) }) => {
    return useQuery({
        queryKey: subscriptionKeys.details(id || ''),
        queryFn: () => getSubscription(id!),
        enabled: !!id,
        refetchInterval: options?.refetchInterval,
    });
};

export const useSubscriptionFromList = (subscriptionId: string, userId: string) => {
    const { data } = useSubscriptions(userId);
    const subscriptions = Array.isArray(data) ? data : [];
    return subscriptions.find(s => s.subscriptionId === subscriptionId);
};



export const useCreateSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createSubscription,
        onSuccess: (newSub) => {
            // Invalidate lists to show the new item
            queryClient.invalidateQueries({ queryKey: subscriptionKeys.all, exact: false, type: 'active' });
            // Optionally prime the detail cache
            if (newSub?.subscriptionId) {
                queryClient.setQueryData(subscriptionKeys.details(newSub.subscriptionId), newSub);
            }
        },
    });
};

export const useUpdateSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => updateSubscription(id, data),
        onSuccess: (updatedSub, { id }) => {
            // 1. Update the specific detail query data immediately (prevents next GET)
            queryClient.setQueryData(subscriptionKeys.details(id), updatedSub);

            // 2. Invalidate only relevant lists, not everything
            // Note: We use exact: false to catch any list query (user specific or global)
            queryClient.invalidateQueries({
                queryKey: [...subscriptionKeys.all, 'list'],
                exact: false
            });
            queryClient.invalidateQueries({
                queryKey: subscriptionKeys.globalList(),
                exact: true
            });
        },
    });
};

export const useDeleteSubscription = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteSubscription,
        onSuccess: (_, id) => {
            // Remove from details cache immediately
            queryClient.removeQueries({ queryKey: subscriptionKeys.details(id) });

            // Optimistically remove the deleted subscription from any cached lists so the UI
            // immediately reflects the deletion even though the backend processes it
            // asynchronously.
            try {
                const queries = queryClient.getQueriesData({ queryKey: subscriptionKeys.all, exact: false });
                for (const [qKey, qData] of queries) {
                    // List-style queries return an array of subscriptions
                    if (Array.isArray(qData)) {
                        queryClient.setQueryData(qKey, (old: any) => {
                            if (!Array.isArray(old)) return old;
                            return old.filter((s: any) => s.subscriptionId !== id);
                        });
                    }

                    // Some admin queries may return an object with a `subscriptions` array
                    else if (qData && Array.isArray((qData as any).subscriptions)) {
                        queryClient.setQueryData(qKey, (old: any) => {
                            if (!old || !Array.isArray(old.subscriptions)) return old;
                            return { ...old, subscriptions: old.subscriptions.filter((s: any) => s.subscriptionId !== id) };
                        });
                    }
                }
            } catch (e) {
                // Swallow any cache-manipulation errors to avoid breaking the app flow
                // (React Query internals may vary between versions).
                // The subsequent invalidate ensures eventual consistency.
                // eslint-disable-next-line no-console
                console.warn('Failed to optimistically remove subscription from cache', e);
            }

            // Ensure queries are refetched to converge with server state
            queryClient.invalidateQueries({ queryKey: subscriptionKeys.all, exact: false });
        },
    });
};

export const useUpdateSubscriptionStatus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
            updateSubscriptionStatus(id, enabled),
        onSuccess: (updatedSub, { id, enabled }) => {
            // Update detail cache
            queryClient.setQueryData(subscriptionKeys.details(id), (old: any) =>
                old ? { ...old, enabled: updatedSub?.enabled ?? enabled } : updatedSub
            );
            // Invalidate lists
            queryClient.invalidateQueries({ queryKey: subscriptionKeys.all, exact: false, type: 'active' });
        },
    });
};
